import { Button } from "@nimit9/signet-ui/components/button"
import { CheckIcon, CopyIcon, DownloadIcon, ShieldAlertIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"

export function MnemonicDisplay({ mnemonic }: { mnemonic: string }) {
  const words = mnemonic.trim().split(/\s+/)
  const [copied, setCopied] = useState(false)
  const [clipboardCleared, setClipboardCleared] = useState(false)
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear pending timers on unmount so they can't fire (and call setState /
  // wipe the clipboard) after the component is gone.
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current)
      if (clearTimer.current) clearTimeout(clearTimer.current)
    },
    [],
  )

  async function handleCopy() {
    await navigator.clipboard.writeText(mnemonic.trim())
    setCopied(true)
    setClipboardCleared(false)

    // Reset any timers from a previous copy before starting fresh ones.
    if (copyTimer.current) clearTimeout(copyTimer.current)
    if (clearTimer.current) clearTimeout(clearTimer.current)

    // Reset the "Copied" button label after 2 s
    copyTimer.current = setTimeout(() => setCopied(false), 2000)

    // Auto-clear clipboard after 60 s so the phrase doesn't linger
    clearTimer.current = setTimeout(() => {
      navigator.clipboard.writeText("").catch(() => {})
      setClipboardCleared(true)
    }, 60_000)
  }

  function handleDownload() {
    const blob = new Blob([mnemonic.trim()], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "paisa-recovery-phrase.txt"
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-3">
      {/* Security warning */}
      <div className="flex items-start gap-2 rounded-md border border-accent-amber/30 bg-accent-amber-subtle px-3 py-2.5">
        <ShieldAlertIcon className="h-4 w-4 shrink-0 mt-0.5 text-accent-amber" />
        <p className="text-caption leading-relaxed text-accent-amber">
          <span className="font-semibold">Treat this phrase like a password.</span> Anyone who has
          it can access your encrypted data. Store it somewhere secure (e.g. a password manager or
          printed paper kept offline) — do not share it or leave it in an email or screenshot.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {words.map((word, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: BIP39 word position is semantically stable
            key={i}
            className="flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-elevated px-2 py-1.5"
          >
            <span className="text-[10px] text-text-hint w-4 text-right">{i + 1}.</span>
            <span className="text-[12px] font-mono text-text-primary">{word}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={handleCopy}>
          {copied ? (
            <>
              <CheckIcon className="h-3.5 w-3.5 text-accent-green" />
              Copied
            </>
          ) : (
            <>
              <CopyIcon className="h-3.5 w-3.5" />
              Copy phrase
            </>
          )}
        </Button>
        <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={handleDownload}>
          <DownloadIcon className="h-3.5 w-3.5" />
          Download
        </Button>
      </div>

      {/* Clipboard-cleared confirmation */}
      {clipboardCleared && (
        <p className="text-center text-[11px] text-text-hint">
          Clipboard cleared — the phrase was removed after 60 seconds.
        </p>
      )}

      {/* Hint shown while copy is in flight */}
      {copied && !clipboardCleared && (
        <p className="text-center text-[11px] text-text-hint">
          Clipboard will be cleared automatically in 60 seconds.
        </p>
      )}
    </div>
  )
}
